import { Component, EventEmitter, ViewContainerRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import * as moment from 'moment';
import { LazyLoadEvent } from 'primeng/api';
import { forkJoin, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ClaimsService } from '../../../auth/services/claims.service';
import { AccessControlType } from '../../../foundation/models/enums/access-control-type.enum';
import { BusinessTransactionType } from '../../../foundation/models/enums/business-transaction-type.enum';
import { CorrespondenceIdentificationType } from '../../../foundation/models/enums/correspondence-identification-type.enum';
import { CustomerStatusType } from '../../../Foundation/models/enums/customer-status-type.enum';
import { NoteModalComponent } from '../../../note/note-modal/note-modal.component';
import { BaseComponent } from '../../../shared/base-components/base.component';
import { DocumentLaunchContext, DocumentLaunchModalComponent } from '../../../shared/components/document/document-launch-modal.component';
import { DocumentItemKeyType } from '../../../shared/enums/document-item-key-type.enum';
import { scrollElementBelowHeader } from '../../../shared/helpers/scroll-helpers.model';
import { getCurrentDate, isStartAfterEnd, setServerDate } from '../../../shared/helpers/time-helpers.model';
import { BusinessTransactionHistorySearchCriteria } from '../../../shared/models/business-transaction/business-transaction-history-search-criteria.model';
import { BusinessTransactionHistorySearchResults } from '../../../shared/models/business-transaction/business-transaction-history-search-results.model';
import { Dictionary } from '../../../shared/models/dictionary.model';
import { DocumentItemKeyData } from '../../../shared/models/document/document-itemkey.model';
import { DocumentSearchCriteria } from '../../../shared/models/document/document-search-criteria.model';
import { BusinessTransactionService } from '../../../shared/services/business-transaction/business-transaction.service';
import { RedirectTransactionService } from '../../../shared/services/business-transaction/redirect-transaction.service';
import { LocationService } from '../../../shared/services/location/location.service';
import { ModalSize } from '../../../shared/services/modal/mvd-modal.enum';
import { MvdModalService } from '../../../shared/services/modal/mvd-modal.service';
import { RedactionService } from '../../../shared/services/redaction/redaction.service';
import { AppSettings } from '../../../shared/settings/app-settings.model';
import { ControlValidationMessagesService } from '../../../shared/validators/control-validation-messages.service';
import { ArtifactContext, ArtifactModalComponent } from '../../../transaction/artifact/artifact-modal.component';
import { BusinessTransactionRealtimeMessagingService } from '../../../transaction/services/business-transaction-realtime-messaging.service';
import { WIPStatusType } from '../../../wip/models/enums/w-i-p-status-type.enum';
import { CustomerAccountService } from '../../account/service/customer-account.service';
import { AuthorizedPresence } from '../../models/authorized-presence.model';
import { CustomerCharacteristic } from '../../models/customer-characteristic.model';
import { CustomerIndividualBiometric } from '../../models/customer-individual-biometric.model';
import { CustomerIndividualName } from '../../models/customer-individual-name.model';
import { CustomerCharacteristicType } from '../../models/enums/customer-characteristic-type.enum';
import { CustomerDetailTab } from '../../models/enums/customer-detail-tab.enum';
import { CustomerIdentificationType } from '../../models/enums/customer-identification-type.enum';
import { CustomerIndividualNameSuffixType } from '../../models/enums/customer-individual-name-suffix-type.enum';
import { CustomerNameType } from '../../models/enums/customer-name-type.enum';
import { CustomerType } from '../../models/enums/customer-type.enum';
import { IndividualDetailViewModel } from '../../models/views/individual-detail-view.model';
import { UspvsModalComponent, UspvsModalContext } from '../../uspvs/uspvs-modal.component';
import { CustomerDetailService } from '../customer-detail.service';
import { IndividualDetailService } from '../individual-detail.service';
import { CustomerPhotoContext, CustomerPhotoModalComponent } from './individual-customer-photo-modal.component';
import { SendCustomerSurveyModalComponent } from '../../contacts/send-customer-survey-modal.component';
import { SecurityTransactionType } from '../../../auth/models/security-transaction-type.enum';

@Component({
    selector: 'individual-customer',
    templateUrl: './individual-customer.html',
    inputs: ['customerNumber', 'inModal'],
    outputs: ['onBackClicked']
})
export class IndividualCustomerComponent extends BaseComponent {
    public individualDetails: IndividualDetailViewModel;
    public showNotes = false;
    public inModal = false;
    public customerNumber = 0;
    public details: any[] = [];
    public names: any[] = [];
    public akas: any[] = [];
    public showAll = false;
    public title: string;
    public titleLink: string;
    public showBack = false;
    public searchDocumentDataCollection: DocumentSearchCriteria[] = [];
    public autoPopupNotesModal = false;
    public form: UntypedFormGroup;
    public customerServiceHistorysearchResults: BusinessTransactionHistorySearchResults[] = [];
    public canEditRedactedRecord = false;
    public canEditAddress = false;
    public tab: CustomerDetailTab = CustomerDetailTab.Customer;
    public tabs: typeof CustomerDetailTab = CustomerDetailTab;
    public rows = 10;
    public dataStore: any = [];
    private previousOrder: number;
    private previousSort = '';
    private previousPageNumber = 0;
    public totalRecords: number;
    public inProgress: boolean;
    public appointmentCreationInProgress: boolean;
    public maxEndDate: Date;
    public maxStartDate: Date;
    public isFullRedactionNecessary = true;

    private fullNameList: any[];
    public nameSuffixTypes = new CustomerIndividualNameSuffixType();
    public customerTypes = new CustomerType();
    private customerNameTypes = new CustomerNameType();
    public accessControlTypes = new AccessControlType();
    public correspondenceIdentificationTypes = new CorrespondenceIdentificationType();
    public securityTransactionType = SecurityTransactionType;
    public showRestricted = true;
    public legacySequenceNumber: string;
    public ssn: string;
    public height: string;
    public weight: string;
    public deceased: boolean;
    public vehicleDetailId: number;
    public customerAccountNumberId: number;
    public deceasedDateExists = false;
    public customer: any[] = [];
    public numberOfAccounts = 0;
    public transactionId = 0;
    public wipStatusType: WIPStatusType = new WIPStatusType();
    onBackClicked: EventEmitter<boolean>;

    public customerStatusTypes: CustomerStatusType = new CustomerStatusType();
    readonly CUSTOMER_TEMPLATE_TYPE_ID: number = 5;

    // Identification Constants
    private custIdentTypes: CustomerIdentificationType = new CustomerIdentificationType();
    public documentUploadItems: DocumentItemKeyData[] = [];

    /**
   * Enum for all business transaction types.
   */
    private businessTransactionType: BusinessTransactionType = new BusinessTransactionType();

    // Gets or sets the business unit list
    public businessUnits: any[];

    // Gets or sets the business transaction type list
    public businessTransactionTypes: any[];

    // Gets or sets the columns for the data table
    public cols = [
        { field: 'receiptId', header: 'Receipt Number' },
        { field: 'businessTransactionCompletionDate', header: 'Service Completion Date' },
        { field: 'businessUnit', header: 'Location' },
        { field: 'businessTransactionType', header: 'Service' },
        { field: 'businessTransactionDetails', header: 'Details' },
        { field: 'agentName', header: 'Agent' },
        { field: 'customerName', header: 'Customer' },
        { field: 'amount', header: 'Amount' }
    ];

    constructor(
        public service: IndividualDetailService,
        private modalService: MvdModalService,
        private viewContainer: ViewContainerRef,
        private _route: ActivatedRoute,
        private redirectTransactionService: RedirectTransactionService,
        private customerAccountService: CustomerAccountService,
        private businessTransactionService: BusinessTransactionService,
        private fb: UntypedFormBuilder,
        private businessTransactionRealtimeMessagingService: BusinessTransactionRealtimeMessagingService,
        private claimsService: ClaimsService,
        private customerDetailService: CustomerDetailService,
        private locationService: LocationService,
        private settings: AppSettings,
        private redactionService: RedactionService
    ) {
        super();
        this.onBackClicked = new EventEmitter<boolean>();
    }

    async ngOnInit() {

        this.inProgress = true;

        this.UseHelpContent('#CONTENT/Customer/Customer_Record.htm%3FTocPath%3DCustomer%7C_____2');
        this.title = 'Customer Record Summary';
        this.titleLink = 'View Full Record';
        this.individualDetails = new IndividualDetailViewModel();
        this.individualDetails.nameDetail = new CustomerIndividualName();
        this.individualDetails.accessControls = new Dictionary([]);

        let id;

        if (this.customerNumber != 0) {
            id = this.customerNumber;
            this.individualDetails.id = id;
            this.showBack = true;
        } else {
            id = this._route.snapshot.params['id'];
            this.individualDetails.id = id;
            this.customerNumber = id;
            const tab: string = this._route.snapshot.params['tab'];

            switch (tab) {
                case 'fleet':
                case 'vehicle':
                    this.tab = this.tabs.Vehicle;
                    break;
                case 'MvdNow':
                    this.tab = this.tabs.MVDNow;
                    break;
                case 'accounts':
                    this.tab = this.tabs.Accounts;
                    break;
                case 'affiliations':
                    this.tab = this.tabs.Affiliations;
                    break;
                case 'documents':
                    this.tab = this.tabs.Documents;
                    break;
                case 'correspondence':
                    this.tab = this.tabs.Correspondence;
                    break;
                case 'notes':
                    this.autoPopupNotesModal = true;
                    break;
                default:
                    break;
            }

            this.autoPopupNotesModal = this._route.snapshot.data['landOnNotes'] === true;

            if (this._route.snapshot.params['transactionId'] != null) {
                this.transactionId = this._route.snapshot.params['transactionId'];
            }

            this.showBack = false;
        }

        this.customerAccountService.getNumberOfAccounts(this.customerNumber)
            .subscribe(result => {
                this.numberOfAccounts = result.result;
            },
            e => {
            },
            () => { }
            );

        if (id !== null && id !== undefined) {
            this.getDetails(id)
                .then(
                    result => {
                        if (result && result.identifications) {
                            this.openUspvsModal(id, result);
                        }
                    },
                    e => this.handleValidationResult(e)
                )
                .catch(err => console.log(err));
        }

        this.customer.push({ customerNumberId: this.customerNumber });

        const documentSearchCriteria1 = new DocumentSearchCriteria();
        documentSearchCriteria1.documentItemKeyTypeId = DocumentItemKeyType.Customer;
        documentSearchCriteria1.itemKeyId = this.customerNumber;
        this.searchDocumentDataCollection.push(documentSearchCriteria1);

        await this.constructFormBuilder();

        this.businessTransactionRealtimeMessagingService.completionResponse$.subscribe(
            r => {
                if (this.customerServiceHistorysearchResults.length > 0 && r) {
                    let completedTransaction = this.customerServiceHistorysearchResults.find(x => r.Id == x.businessTransactionNumber);
                    if (completedTransaction !== undefined) {
                        completedTransaction.wipStatusTypeId = this.wipStatusType.ServiceComplete.id;
                    }
                }
            });

        this.inProgress = false;
    }

    private getDetails = (customerNumber): Promise<any> => {
        const resultPromise = new Promise((resolve, reject) => {
            const detailsObservable: Observable<any> = this.service.getData(customerNumber);
            const claimsObservable: Observable<any> = this.claimsService.getACSClaims();
            forkJoin(detailsObservable, claimsObservable)
                .subscribe(([individualDetails, claimsData]) => {
                    this.individualDetails = individualDetails;
                    let claims: string[] = claimsData.result.acsClaims.map(uc => uc.value);
                    this.setEditabilityOfChildren(claims, this.individualDetails.characteristics);
                    this.initializeNotesModal();

                    // Set defaults of any null properties
                    this.individualDetails.biometric = this.individualDetails.biometric || new CustomerIndividualBiometric();
                    this.individualDetails.authorizedPresence = this.individualDetails.authorizedPresence || new AuthorizedPresence();

                    this.ssn = this.getIdentificationValue(this.custIdentTypes.SSN.id);
                    this.legacySequenceNumber = this.getIdentificationValue(this.custIdentTypes.LegacySequenceNumber.id);

                    let age;
                    if (this.individualDetails.biometric && this.individualDetails.biometric.dateOfBirth) {
                        age = this.getAge(this.individualDetails.biometric.dateOfBirth, this.individualDetails.biometric.deceasedDate);
                    }

                    if (this.individualDetails.biometric && this.individualDetails.biometric.deceasedDate) {
                        this.deceased = true;
                    }

                    this.details = [];
                    this.details.push({
                        'customerType': this.getValues(this.individualDetails.customerTypeId, this.customerTypes),
                        'customerNumber': this.individualDetails.id,
                        'dateOfBirth': this.individualDetails.biometric.dateOfBirth,
                        'deceasedDate': this.individualDetails.biometric.deceasedDate,
                        'age': age,
                        'ssn': this.ssn,
                        'isBiometricRedacted': this.individualDetails.biometric['isBiometricRedacted']
                    });

                    this.names = [];
                    this.names.push(this.individualDetails.nameDetail);
                    this.names = this.names.concat(this.individualDetails.akas);
                    this.hideRestrictedItems(this.individualDetails.characteristics, claims);
                    this.isFullRedactionNecessary = this.redactionService.isFullRedactionNecessary(this.individualDetails.characteristics);

                    resolve(this.individualDetails);
                });
        });

        return resultPromise;
    };

    hideRestrictedItems(characteristics: CustomerCharacteristic[], claims: string[]) {
        this.showRestricted = !(characteristics.some(x => x.customerCharacteristicTypeId == 40) && !claims.includes('LECURestrictions'));
    }

    getValues(id: number, types: any): string {
        if (id !== undefined && id !== null) {
            let type = types.FromId(id);

            if (null != type) {
                return type.displayValue;
            }
        }

        return '';
    }

    public changeTab(tab: CustomerDetailTab, id: number = null) {
        if (id) {
            if (tab == this.tabs.VehicleDetails) {
                this.vehicleDetailId = id;
            } else if (tab >= this.tabs.Accounts) {
                this.customerAccountNumberId = id;
            }
        }
        // this.showBack = tab != this.tabs.Customer;
        this.tab = tab;
        if (tab == this.tabs.DriverInformation) {
            this.UseHelpContent('#CONTENT/Driver/Credential_History.htm%3FTocPath%3DDriver|_____4');
        } else {
            this.UseHelpContent('#CONTENT/Customer/Customer_Record.htm%3FTocPath%3DCustomer%7C_____2');
        }
    }

    navigateBack() {
        if (this.tab == this.tabs.AccountDetails) {
            this.tab = this.tabs.Accounts;
        } else if (this.tab == this.tabs.AccountEdit || this.tab == this.tabs.DisbursementRequest || this.tab == this.tabs.ServiceHistory) {
            this.tab = this.tabs.AccountDetails;
        } else if (this.tab == this.tabs.VehicleDetails) {
            this.tab = this.tabs.Vehicle;
        } else if (this.tab == this.tabs.Customer) {
            this.onBackClicked.emit(true);
        } else {
            this.tab = this.tabs.Customer;
        }
    }

    toggleView() {
        if (this.showAll) {
            this.title = 'Customer Record Summary';
            this.titleLink = 'View Full Record';
            this.showAll = false;
        } else {
            this.title = 'Full Customer Record';
            this.titleLink = 'View Summary';
            this.showAll = true;
        }
        this.tab = this.tabs.Customer;
    }

    private setEditabilityOfChildren(claims: string[], characteristics: CustomerCharacteristic[]) {
        const cTypes: CustomerCharacteristicType = new CustomerCharacteristicType();
        this.canEditRedactedRecord = claims.includes('UpdRestrictedRecord');

        const isACP = characteristics.some(c => c.customerCharacteristicTypeId === cTypes.AddressConfidentialityProgram.id) && !claims.includes('UpdACP');
        const isOOPIAH = characteristics.some(c => c.customerCharacteristicTypeId === cTypes.OOPIAH.id) && !claims.includes('UpdOOPIAH');
        this.canEditAddress = !(isACP || isOOPIAH) && this.individualDetails.accessControls[this.accessControlTypes.UpdCustAddress.id];
    }

    showPhotoModal() {
        const data = new CustomerPhotoContext(this.individualDetails.id, this.individualDetails && this.individualDetails.nameDetail);
        this.modalService.open(CustomerPhotoModalComponent, data, ModalSize.Medium);
    }

    goTo(location: string) {
        const element = document.getElementById(location);
        scrollElementBelowHeader(element);
    }

    acpChanged(isACP) {
        this.individualDetails.isACP = isACP;

        this.getDetails(this.individualDetails.id);
    }

    reloadData(yes) {

        this.getDetails(this.individualDetails.id);
    }

    deceasedChanged(dead) {
        this.deceased = dead;
    }

    statusChanged(customerStatusTypeId) {
        this.individualDetails.statusTypeId = customerStatusTypeId;
    }

    private getIdentificationValue(identId: number): string {
        if (this.individualDetails && this.individualDetails.identifications) {
            let ident = this.individualDetails.identifications.find(id => id.customerIdentificationTypeId === identId);
            return ident && ident.customerIdentificationValue ? ident.customerIdentificationValue : '';
        }
        return '';
    }

    private openUspvsModal(id, data: any) {
        const legacySequenceNumber = this._route.snapshot.params['legacySequenceNumber'];
        const uspvsModalContext = new UspvsModalContext(legacySequenceNumber, id);

        const passportNumber = data.identifications.find(x => x.customerIdentificationTypeId === this.custIdentTypes.PassportID);
        const dob = new Date(data.biometric.dateOfBirth);
        uspvsModalContext.dateOfBirth = (dob.getMonth() + 1) + '/' + dob.getDate() + '/' + dob.getFullYear();
        uspvsModalContext.passportId = passportNumber ? passportNumber.customerIdentificationValue : undefined;

        if (this._route.snapshot.params['showUspvs'] === 'true') {
            this.modalService.open(UspvsModalComponent, uspvsModalContext);
        }
    }

    openNotesModal() {
        this.modalService.open(NoteModalComponent, this.individualDetails.id);
    }

    openSendCustomerSurveyModal(): void {
        this.modalService.open(SendCustomerSurveyModalComponent, this.individualDetails.id);
    }

    initializeNotesModal() {
        if (this.autoPopupNotesModal) {
            this.autoPopupNotesModal = false;
            this.openNotesModal();
        }
    }

    public customerAddressesUpdated = (addresses: any[]): void => {
        this.individualDetails.addresses = addresses;
        // Refreshing customer details
        this.getDetails(this.individualDetails.id);
    };

    public backToTransaction() {
        this.redirectTransactionService.redirectToTransaction('' + this.transactionId, this.viewContainer);
    }

    private async constructFormBuilder() {

        this.form = this.fb.group({
            customerNumberId: [this.customerNumber],
            startDate: [setServerDate(moment(getCurrentDate()).subtract(90, 'day').toDate()), Validators.compose([ControlValidationMessagesService.minDateValidator])],
            endDate: [setServerDate(moment(getCurrentDate()).toDate()), Validators.compose([ControlValidationMessagesService.minDateValidator])],
            businessUnitId: [null],
            businessTransactionTypeId: [null],
            receiptId: [null, Validators.compose([ControlValidationMessagesService.positiveNumber])]
        },
        {
            validators: this.dateValidator
        });

        this.getBusinessTransactionTypes();
        await this.getBusinessUnits();
        this.setDefaultControls();
    }

    private dateValidator(group: UntypedFormGroup): ValidationErrors | null {
        const startDate = group.get('startDate').value;
        const endDate = group.get('endDate').value;
        if (isStartAfterEnd(startDate, endDate)) {
            return { 'startAfterEndDate': true };
        }
        return null;
    }

    private setEndDateMaxValue() {
        const endDate = moment(this.form.get('startDate').value).add(90, 'day');
        this.maxEndDate = (endDate.diff(moment(getCurrentDate()), 'days') > 0) ? getCurrentDate() : setServerDate(endDate.toDate());
        this.form.get('endDate').setValue(this.maxEndDate);
    }

    setDefaultControls() {
        this.form.controls['customerNumberId'].setValue(this.customerNumber);
        this.form.controls['startDate'].setValue(setServerDate(moment(getCurrentDate()).subtract(90, 'day').toDate()));
        this.form.controls['endDate'].setValue(setServerDate(moment(getCurrentDate()).toDate()));
        this.setEndDateMaxValue();
        this.maxStartDate = getCurrentDate();
    }

    /**
    * Handles the on start date changed event of the date picker
    * and sets the end date and max end date value
    */
    public onStartDateChanged(event) {
        this.setEndDateMaxValue();
    }

    resetSearch() {
        this.form.reset();
        this.setDefaultControls();
        this.resetPagination();
    }

    /**
     * Reset pagination settings
     */
    public resetPagination(): void {
        this.dataStore = [];
        this.previousPageNumber = -1;
        this.totalRecords = 0;
    }

    search() {
        const event: LazyLoadEvent = {
            first: 0,
            rows: this.rows,
            sortOrder: 0
        };
        this.resetPagination();
        this.populateSearchResults(event);
    }

    loadDataLazy(event: LazyLoadEvent) {
        this.populateSearchResults(event);
    }

    private populateSearchResults(event: LazyLoadEvent) {
        if (this.form.valid) {

            const searchCriteria: BusinessTransactionHistorySearchCriteria = this.form.value;
            searchCriteria.lazyLoadEvent = event;

            // Set page load in progress
            this.inProgress = true;

            // Calculate the current page used for determing if we are going to load from the data store or fetch data
            const pageNumber = Math.floor(event.first / this.rows);

            // if all the sorting is the same and we are on a previous page then just reload from the data store
            // otherwise go fetch data
            if (this.previousSort == event.sortField && this.previousOrder == event.sortOrder && this.previousPageNumber >= pageNumber) {
                this.displaySearchResults(event);
            } else {
                // searchCriteria.lazyLoadEvent.first = pageNumber;

                this.businessTransactionService.searchBusinessTransactionHistoryForCustomer(searchCriteria)
                    .subscribe(result => {
                        this.title = 'Customer Service History';

                        // Splice these into the data store in case this is a change for sorting
                        // so the existing records will be kept
                        this.dataStore.splice(event.first, event.rows, ...result.result);

                        // calculate the total records used by the grid if we new data coming in.
                        if (result.result.length > 0) {
                            this.totalRecords = (pageNumber + 1) * this.rows + 1;
                        }

                        this.displaySearchResults(event);
                    },
                    error => {
                        this.customerServiceHistorysearchResults = null;
                        this.inProgress = false;
                        this.handleValidationResult(error);
                    });
            }

            // Set our previous page information
            this.previousPageNumber = pageNumber;
            this.previousSort = event.sortField;
            this.previousOrder = event.sortOrder;
        }
    }

    private displaySearchResults(event: LazyLoadEvent) {

        this.inProgress = false;
        this.customerServiceHistorysearchResults = [];

        // Slice the results and apply the current filter.
        this.customerServiceHistorysearchResults = this.dataStore.slice(event.first, (event.first + event.rows));
        this.customerServiceHistorysearchResults = this.customerServiceHistorysearchResults.filter(row => this.filterGlobal(row, event.globalFilter));
    }

    public filterGlobal(row, value) {
        for (let i = 0; i < this.cols.length; i++) {
            let column: any = this.cols[i];
            if (row[column.field] == null) {
                continue;
            }
            let rowValue: String = row[column.field].toString().toLowerCase();
            if (value == null || rowValue.includes(value.toLowerCase())) {
                return true;
            }
        }
        return false;
    }

    getArtifacts(businessTransactionNumber: number, details: string) {
        const data = new ArtifactContext(businessTransactionNumber, details);
        this.modalService.open(ArtifactModalComponent, data, ModalSize.Medium);
    }

    retryCompleteBusinessTransaction(transaction: BusinessTransactionHistorySearchResults) {
        transaction.wipStatusTypeId = null;
        this.businessTransactionService.completeBusinessTransaction(transaction.businessTransactionNumber)
            .subscribe(result => {
                // Wait for completionResponse$ event to be fired for the transaction.
            },
            err => {
            });
    }

    public launchDocuments(businessTransactionNumberId: number) {
        var item = new DocumentItemKeyData();
        item.documentItemKeyTypeId = 7;
        item.itemKeyId = businessTransactionNumberId;
        this.documentUploadItems.push(item);

        let data = new DocumentLaunchContext(this.documentUploadItems, this.customerNumber);

        // open the modal
        this.modalService.open(DocumentLaunchModalComponent, data)
            .afterClosed()
            .subscribe(() => { });
    }

    public async createAppointment() {
        if (!this.appointmentCreationInProgress) {
            this.appointmentCreationInProgress = true;
            let cNumber = this.customerNumber.toString();
            let fName = this.individualDetails.nameDetail.firstName;
            let lName = this.individualDetails.nameDetail.lastName;
            await this.customerDetailService.checkForCustomerAndCreateAppointment(cNumber, fName, lName)
                .pipe(finalize(() => {
                    this.appointmentCreationInProgress = false;
                }))
                .subscribe(
                    result => {
                        if (result.result == true) {
                            this.goToAppointmentCreation(cNumber);
                        }
                    }
                );
        }
    }

    public goToAppointmentCreation(customerNumberId: string) {
        const url = `${this.settings.asqAppointmentsUrl}?customerExternalId=${customerNumberId}&locationExternalId=${this.auth.businessUnitId}`;
        window.open(url, 'ASQ');
    }

    /**
     * Gets the business transaction types
     */
    private getBusinessTransactionTypes() {
        const result = this.businessTransactionType.GetActive();
        this.businessTransactionTypes = [];
        for (let businessTransactionType of result) {
            this.businessTransactionTypes.push({ value: businessTransactionType.id.toString(), label: businessTransactionType.displayValue.toString() });
        }

        // Manually insert empty business transaction type to drop down list
        const businessTransactionType = { value: null, label: '' };
        this.businessTransactionTypes.unshift(businessTransactionType);
    }

    /**
     * Gets the current business units.
     */
    private async getBusinessUnits() {
        var result = await this.locationService.getActiveBusinessUnits().toPromise();

        result.result.forEach(businessUnit => {
            businessUnit.value = businessUnit.id;
            businessUnit.label = '(' + businessUnit.businessUnitCode + ') ' + businessUnit.businessUnitName;
        });

        this.businessUnits = result.result;
        // Manually insert empty business unit to drop down list
        const businessUnit = { value: null, label: '' };
        this.businessUnits.unshift(businessUnit);
    }
}
